import React, { useState, useMemo, useCallback } from 'react';
import { object } from 'prop-types';
import { NavLink as BaseNavLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames/bind';
import {
  faBorderAll,
  faStream,
  faChevronDown,
  faSquareRootAlt,
  faLightbulb,
  faThLarge,
} from '@fortawesome/free-solid-svg-icons';
import { faBell } from '@fortawesome/free-regular-svg-icons';
import routes from '../../routes';
import Icon from '../Shared/Icon';
import userActions from '../../actions/userActions';
import nomoneyLogoImage from '@images/nomoney.svg';
import { userSelector } from '../../selectors/userSelectors';
import { darkThemeSelector } from '../../selectors/themeSelectors';
import Avatar from '../Shared/Avatar/Avatar';
import PopupBalloon, {
  popupBalloonEvents,
  popupBalloonPresets,
} from '../Shared/PopupBalloon';
import { themeSlice } from '../../reducers/themeSlice';
import Toast from '../Shared/Alerts/Toast';
import userService from '../../services/userService';
import { defaultErrorHandler, defaultSuccessHandler } from '../../services/api';
import { ActionCableConsumer } from '../Shared/ActionCable';
import MenuButton from './MenuButton/MenuButton';
import SettingsMenu from './SettingsMenu/SettingssMenu';
import navbarStyles from './Navbar.module.scss';
import MainMenu from './MainMenu/MainMenu';

const NavLink = ({ route: { path, name }, icon }) => (
  <BaseNavLink
    to={path}
    className={navbarStyles.link}
    activeClassName={navbarStyles.active}
    target="_self"
  >
    <Icon use={icon} classes="small" />
    {name}
  </BaseNavLink>
);

const Navbar = () => {
  const dispatch = useDispatch();

  const user = useSelector(userSelector);

  const isDarkTheme = useSelector(darkThemeSelector);

  const [displayNotifications, setDisplayNotifications] = useState(false);
  const [displayUserMenu, setDisplayUserMenu] = useState(false);

  const boundStyles = classNames.bind(navbarStyles);

  const themeClasses = boundStyles({
    themeToggler: true,
    darkMode: isDarkTheme,
  });

  const handleToggleTheme = () => {
    document.querySelector('body').classList.toggle('dark');
    dispatch(themeSlice.actions.set(isDarkTheme ? 'light' : 'dark'));
  };

  const handleReceivedEvent = useCallback((message) => {
    if (message.type === 'ALERT') {
      Toast.info(message.text);
    } else if (message.type === 'NOTIFICATION') {
      dispatch(userActions.notify({ text: message.text }));
    } else if (message.type === 'CLEAR_CONFIRMATION_NOTIFICATION') {
      dispatch(userActions.denotify({ id: 1 }));
    }
  }, [dispatch]);

  const handleNotificationClick = (notification) => {
    if (notification.action === 'confirm-account') {
      userService
        .resendConfirmationEmail()
        .then(defaultSuccessHandler(Toast.success))
        .catch(defaultErrorHandler(Toast.error));
    }

    setDisplayNotifications(false);
  };

  const actionCableChannel = useMemo(
    () => (
      <ActionCableConsumer
        channel={{ channel: 'EventsChannel' }}
        onReceived={handleReceivedEvent}
      />
    ),
    [handleReceivedEvent],
  );

  return (
    <div className={navbarStyles.wrapper}>
      {actionCableChannel}
      <nav className={navbarStyles.bar}>
        <div className={navbarStyles.left}>
          <BaseNavLink to={routes.dashboard.path}>
            <img src={nomoneyLogoImage} alt='NoMoney' aria-label="NoMoney" />
          </BaseNavLink>
        </div>
        <div className={navbarStyles.center}>
          <div className={navbarStyles.group}>
            <MainMenu />
          </div>
        </div>
        <div className={navbarStyles.right}>
          <div className={navbarStyles.actionCenter}>
            <div className={navbarStyles.actionCenterIcon}>
              <div onClick={handleToggleTheme} className={themeClasses}>
                <Icon use={faLightbulb} />
              </div>
            </div>
            <div className={navbarStyles.actionCenterIcon}>
              <div onClick={() => setDisplayNotifications(!displayNotifications)}>
                {user?.notifications?.length > 0 && (
                  <span className={navbarStyles.actionCenterIconBadge}></span>
                )}
                <Icon use={faBell} />
              </div>
              <PopupBalloon
                title="Notifications"
                preset={popupBalloonPresets.navbarLarge}
                dismiss={popupBalloonEvents.mouseDown}
                visible={displayNotifications}
                setVisibility={setDisplayNotifications}
              >
                {(user?.notifications?.length > 0 &&
                  user.notifications.map((notification, index) => (
                    <div
                      key={`notification-${index}`}
                      className={navbarStyles.notification}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      {notification.text}
                    </div>
                  ))) || (
                  <div className={navbarStyles.nothingToShow}>
                    Nothing's happening right now...
                  </div>
                )}
              </PopupBalloon>
            </div>
            <MenuButton />
            <Avatar
              className={navbarStyles.actionCenterAvatar}
              seed={user.avatar_seed}
              onClick={() => setDisplayUserMenu(!displayUserMenu)}
            />
            <div className={navbarStyles.actionCenterItem}>
              <div onClick={() => setDisplayUserMenu(!displayUserMenu)}>
                <div className={navbarStyles.userName}>
                  {user.first_name} {user.last_name}{' '}
                  <Icon
                    use={faChevronDown}
                    transform="shrink-6 right-3 top-3"
                  />
                </div>
                <div
                  className={boundStyles({
                    subscriptionType: true,
                    premium: user.subscription_type === 'Premium',
                    free: user.subscription_type !== 'Premium',
                  })}
                >
                  {user.subscription_type}
                </div>
              </div>
              <PopupBalloon
                title="Actions"
                preset={popupBalloonPresets.navbar}
                dismiss={popupBalloonEvents.mouseDown}
                visible={displayUserMenu}
                setVisibility={setDisplayUserMenu}
              >
                <SettingsMenu />
              </PopupBalloon>
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
};

Navbar.propTypes = {
  routes: object,
};

export default Navbar;
